import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import styles from "./equipmentWeekGrid.module.css";
import moment from "moment";
import { Text } from "ui/Typography";
import API from "constants/api";
import EventBlock from "./EventBlock";
import { getEventColor, getEventBorderColor } from "pagesV2/pro/Calendar/utils";
import BookingDetailsModal from "pagesV2/pro/clients/ClientDetails/ClientDetailsTabs/BookingTab/BookingTable/Modals/BookingDetails";
import { useRefreshCalendarBookings } from "pagesV2/pro/Calendar/useRefreshCalendarBookings";

const EQUIPMENT_API = `${API.BASE_URL}/guide/boats/listing?lang=en-GB`;

const isEventForEquipmentAndDay = (ev, eq, day) => {
  if (!moment(ev.date).isSame(day, "day")) return false
  const assigned = ev.assigned_equipment || []
  return assigned.some((item) => item.name === eq.name || item.boat_id === eq.id)
}

const formatEquipmentEventLabels = (ev) => {
  const startMoment = ev.start ? moment(ev.start, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"]) : null
  const endMoment = ev.end ? moment(ev.end, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"]) : null
  return {
    ...ev,
    startLabel: startMoment?.isValid() ? startMoment.format("h:mm A") : ev.start,
    endLabel: endMoment?.isValid() ? endMoment.format("h:mm A") : ev.end,
  }
}

const buildEquipmentDayMap = (eq, calendarDays, events) => {
  const dayMap = {}
  calendarDays.forEach((day) => {
    const dayStr = day.format("YYYY-MM-DD")
    dayMap[dayStr] = events.filter((ev) => isEventForEquipmentAndDay(ev, eq, day)).map(formatEquipmentEventLabels)
  })
  return dayMap
}

// EquipmentWeekGrid: Equipment as rows, days as columns, table layout
const EquipmentWeekGrid = ({ calendarDays, events }) => {
  const [equipmentList, setEquipmentList] = useState([]);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    const fetchOptions = token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
    fetch(EQUIPMENT_API, fetchOptions)
      .then((r) => r.json())
      .then((res) => {
        const items = (res.data || []).map((eq) => ({ id: eq.id, name: eq.equipment_title }));
        setEquipmentList(items);
      })
      .catch(() => {});
  }, []);

  // Build lookup: equipment name -> day -> normalized events
  const eventsByEquipmentAndDay = useMemo(() => {
    const map = {};
    equipmentList.forEach((eq) => {
      map[eq.name] = buildEquipmentDayMap(eq, calendarDays, events);
    });
    return map;
  }, [equipmentList, calendarDays, events]);

  const refreshCalendarBookings = useRefreshCalendarBookings()
  const handleBookingCancel = () => {
    setIsBookingModalOpen(false);
    setSelectedBookingId(null);
  };
  const handleBookingCancelSuccess = () => { handleBookingCancel(); refreshCalendarBookings() }

  return (
    <div className={styles.equipmentWeekGrid}>
      <table className={styles.equipmentWeekTable}>
        <thead>
          <tr className={styles.equipmentWeekHeaderRow}>
            <th className={styles.equipmentWeekHeaderCell}>Equipment</th>
            {calendarDays.map((day) => (
              <th key={day.format("YYYY-MM-DD")} className={styles.equipmentWeekHeaderCell}>
                <Text fw="500">{day.format("ddd D")}</Text>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={styles.equipmentWeekBody}>
          {equipmentList.length === 0 ? (
            <tr className={styles.equipmentWeekRow}>
              <td colSpan={calendarDays.length + 1} className={styles.equipmentWeekEmptyCell}>
                No equipment found.
              </td>
            </tr>
          ) : (
            equipmentList.map((eq) => (
              <tr key={eq.id} className={styles.equipmentWeekRow}>
                <td className={styles.equipmentWeekEquipmentCell}>
                  <Text>{eq.name}</Text>
                </td>
                {calendarDays.map((day) => {
                  const dayStr = day.format("YYYY-MM-DD");
                  const cellEvents = eventsByEquipmentAndDay[eq.name]?.[dayStr] || [];
                  return (
                    <td key={dayStr} className={styles.equipmentWeekEventCell}>
                      <div className={styles.equipmentWeekEventCellInner}>
                        {cellEvents.map((ev) => (
                          <EventBlock
                            key={ev.id ?? (ev.title + ev.start)}
                            event={ev}
                            eventColor={getEventColor(ev.payment_status)}
                            eventBorderColor={getEventBorderColor(ev.payment_status)}
                            setIsBookingModalOpen={setIsBookingModalOpen}
                            setSelectedBookingId={setSelectedBookingId}
                            compact
                          />
                        ))}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>

      {isBookingModalOpen && (
        <BookingDetailsModal
          open={isBookingModalOpen}
          handleOnCancel={handleBookingCancel}
          bookingUuid={selectedBookingId}
          onCancelSuccess={handleBookingCancelSuccess}
        />
      )}
    </div>
  );
};

EquipmentWeekGrid.propTypes = {
  calendarDays: PropTypes.array.isRequired,
  events: PropTypes.array.isRequired,
};

export default EquipmentWeekGrid;
